import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Camera, Briefcase, Ship, Star, Users, Award } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function HomePage() {
  const [destinations, setDestinations] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, servRes, prodRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/services`),
          axios.get(`${API}/products`)
        ]);
        // Only show first 3 destinations on homepage for clean bento grid layout
        setDestinations(destRes.data.slice(0, 3));
        // Show all 4 services
        setServices(servRes.data);
        setProducts(prodRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/18619490/pexels-photo-18619490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Raja Ampat Indonesia aerial view"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="label-style text-white/80 mb-4"
            >
              Discover Indonesia
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl text-white mb-6 leading-tight"
            >
              <span className="text-[#D92525]">Where Paradise</span>
              <br />
              Meets Adventure
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-white/80 mb-10 max-w-xl leading-relaxed"
            >
              Explore 17,000 islands of breathtaking beauty. From ancient temples to pristine beaches, 
              from business opportunities to unforgettable adventures.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-4"
            >
              <Link 
                to="/destinations" 
                data-testid="hero-explore-btn"
                className="btn-primary flex items-center gap-2"
              >
                Explore Destinations
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/membership" 
                data-testid="hero-join-btn"
                className="btn-secondary"
              >
                Join Our Community
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md py-8"
        >
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: MapPin, value: "17,000+", label: "Islands" },
                { icon: Users, value: "10,000+", label: "Happy Travelers" },
                { icon: Star, value: "4.9", label: "Average Rating" },
                { icon: Award, value: "15+", label: "Years Experience" },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-6 h-6 mx-auto mb-2 text-[#1B4D3E]" />
                  <p className="text-2xl md:text-3xl font-['Playfair_Display'] font-semibold text-[#0E2954]">
                    {stat.value}
                  </p>
                  <p className="text-sm text-[#666666]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Destinations Section */}
      <section 
        data-testid="destinations-section"
        className="section-padding bg-white"
      >
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style mb-2">
              Iconic Destinations
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <h2 className="text-3xl md:text-5xl max-w-xl mb-4 md:mb-0">
                Discover Indonesia's Hidden Treasures
              </h2>
              <Link 
                to="/destinations"
                data-testid="destinations-view-all-link"
                className="text-[#0E2954] font-medium flex items-center gap-2 link-hover"
              >
                View All Destinations
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Bento Grid */}
            <div className="bento-grid">
              {destinations.map((dest, index) => (
                <motion.div
                  key={dest.id}
                  variants={fadeInUp}
                  data-testid={`destination-card-${dest.id}`}
                  className={`relative overflow-hidden rounded-lg group cursor-pointer ${
                    index === 0 ? "min-h-[400px] md:min-h-[500px]" : "min-h-[200px] md:min-h-[240px]"
                  }`}
                >
                  <img 
                    src={dest.image_url}
                    alt={dest.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="image-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                    <h3 className="text-white text-2xl md:text-3xl mb-2">{dest.name}</h3>
                    <p className="text-white/80 text-sm line-clamp-2">{dest.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        data-testid="services-section"
        className="section-padding"
      >
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style mb-2">
              Our Services
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <h2 className="text-3xl md:text-5xl max-w-xl mb-4 md:mb-0">
                Complete Tourism & Business Solutions
              </h2>
              <Link 
                to="/services"
                data-testid="services-view-all-link"
                className="text-[#0E2954] font-medium flex items-center gap-2 link-hover"
              >
                View All Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  data-testid={`service-card-${service.id}`}
                  className="service-card rounded-lg overflow-hidden"
                >
                  <div className="aspect-square overflow-hidden bg-gray-50 flex items-center justify-center p-4">
                    <img 
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl mb-3">{service.name}</h3>
                    <p className="text-[#666666] text-sm mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.slice(0, 2).map((feature, i) => (
                        <span 
                          key={i}
                          className="text-xs px-3 py-1 bg-[#0E2954]/5 text-[#0E2954] rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section 
        data-testid="products-section"
        className="section-padding bg-[#1B4D3E]"
      >
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/60 mb-2">
              Premium Products
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <h2 className="text-3xl md:text-5xl max-w-xl text-white mb-4 md:mb-0">
                From Souvenirs to Luxury Ships
              </h2>
              <Link 
                to="/products"
                data-testid="products-view-all-link"
                className="text-white font-medium flex items-center gap-2 link-hover"
              >
                Browse All Products
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  variants={fadeInUp}
                  data-testid={`product-card-${product.id}`}
                  className="product-card group"
                >
                  <img 
                    src={product.image_url}
                    alt={product.name}
                    className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                    <span className="text-xs text-white/60 uppercase tracking-widest">
                      {product.category}
                    </span>
                    <h3 className="text-white text-xl mt-1">{product.name}</h3>
                    <p className="text-white/80 text-sm mt-2">{product.price_range}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="section-padding"
      >
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-[#0E2954] rounded-2xl overflow-hidden"
          >
            <div className="grid md:grid-cols-2">
              <div className="p-12 md:p-16 flex flex-col justify-center">
                <h2 className="text-3xl md:text-4xl text-white mb-6">
                  Ready to Start Your Indonesian Adventure?
                </h2>
                <p className="text-white/70 mb-8 leading-relaxed">
                  Join our exclusive membership to get personalized recommendations, 
                  special offers, and insider access to Indonesia's best experiences.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/membership"
                    data-testid="cta-membership-btn"
                    className="btn-primary"
                  >
                    Become a Member
                  </Link>
                  <Link 
                    to="/about"
                    data-testid="cta-about-btn"
                    className="text-white font-medium flex items-center gap-2"
                  >
                    Learn More About Us
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="relative min-h-[300px] md:min-h-full">
                <img 
                  src="https://images.pexels.com/photos/2583852/pexels-photo-2583852.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Raja Ampat crystal clear waters"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
